"use client";

import { Checkbox } from "@/components/tool-kit/primitives";
import type { DoiFinderStrings } from "@/components/tools/doi-finder";
import {
  Badge,
  type BadgeTone,
} from "@/components/tools/reference-tools/badge";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BestMatch,
  doiUrl,
} from "@/lib/tool-kit/reference-tools/doi-finder";
import type { FetchFailure } from "@/lib/tool-kit/reference-tools/network";

/*
 * One reference in the DOI Finder: its state, and for a match the Crossref record set
 * beside it with the three checks that produced the verdict. Every word comes from the
 * widget's strings.
 */

export type DoiRowState =
  | "queued"
  | "hasDoi"
  | "short"
  | "over"
  | "done"
  | "failed";

export interface DoiRow {
  id: number;
  reference: string;
  state: DoiRowState;
  match: BestMatch | null;
  accepted: boolean;
  reason?: FetchFailure;
}

type StateKey = keyof DoiFinderStrings["states"];

function stateKey(row: DoiRow): StateKey {
  if (row.state === "done") {
    return row.match ? row.match.level : "none";
  }
  return row.state;
}

const TONES: Record<StateKey, BadgeTone> = {
  confident: "good",
  uncertain: "warn",
  none: "neutral",
  hasDoi: "info",
  short: "neutral",
  over: "neutral",
  failed: "bad",
  queued: "neutral",
};

const LINK = "break-all text-primary underline-offset-4 hover:underline";

function rowNote(row: DoiRow, strings: DoiFinderStrings): string | null {
  if (row.state === "failed") {
    return strings.failures[row.reason ?? "unavailable"];
  }
  if (row.state === "done" && !row.match) {
    return strings.notes.none;
  }
  if (row.state === "hasDoi" || row.state === "short" || row.state === "over") {
    return strings.notes[row.state];
  }
  return null;
}

function Checks({
  match,
  strings,
}: {
  match: BestMatch;
  strings: DoiFinderStrings;
}) {
  const fmt = useToolNumber();
  const { score, candidate } = match;
  const name = candidate.authors[0] ?? "";
  const year = candidate.year === null ? "" : String(candidate.year);
  const author = {
    true: t(strings.checks.authorYes, { name }),
    false: t(strings.checks.authorNo, { name }),
    null: strings.checks.authorNone,
  }[String(score.author) as "true" | "false" | "null"];
  const years = {
    exact: t(strings.checks.yearExact, { year }),
    near: t(strings.checks.yearNear, { year }),
    no: t(strings.checks.yearNo, { year }),
    null: strings.checks.yearUnknown,
  }[score.year ?? "null"];
  return (
    <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-xs">
      <li>
        {t(strings.checks.title, {
          share: fmt(score.title, {
            style: "percent",
            maximumFractionDigits: 0,
          }),
        })}
      </li>
      <li>{author}</li>
      <li>{years}</li>
    </ul>
  );
}

function Record({
  match,
  strings,
}: {
  match: BestMatch;
  strings: DoiFinderStrings;
}) {
  const { candidate } = match;
  const authors = candidate.authors.slice(0, 3).join(", ");
  const more = candidate.authors.length > 3 ? ` ${strings.etAl}` : "";
  const url = doiUrl(candidate.doi);
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium text-foreground">{candidate.title}</p>
      <p className="text-muted-foreground">
        {[`${authors}${more}`, candidate.year, candidate.container]
          .filter((part) => part !== "" && part !== null)
          .join(" · ")}
      </p>
      <a className={LINK} href={url} rel="noopener noreferrer" target="_blank">
        {url}
      </a>
    </div>
  );
}

export function DoiRowCard({
  row,
  strings,
  onToggle,
}: {
  row: DoiRow;
  strings: DoiFinderStrings;
  onToggle: (id: number, accepted: boolean) => void;
}) {
  const key = stateKey(row);
  const note = rowNote(row, strings);
  const match = row.state === "done" ? row.match : null;
  return (
    <li className="space-y-3 rounded-lg border border-border bg-card p-3">
      <Badge tone={TONES[key]}>{strings.states[key]}</Badge>
      {match ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {strings.yourReference}
            </p>
            <p className="break-words text-foreground text-sm">
              {row.reference}
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {strings.crossrefRecord}
            </p>
            <Record match={match} strings={strings} />
            <Checks match={match} strings={strings} />
            <Checkbox
              checked={row.accepted}
              label={strings.accept}
              onChange={(checked) => onToggle(row.id, checked)}
            />
          </div>
        </div>
      ) : (
        <p className="break-words text-foreground text-sm">{row.reference}</p>
      )}
      {note ? <p className="text-muted-foreground text-xs">{note}</p> : null}
    </li>
  );
}
